import { useQuery } from '@tanstack/react-query'
import { Link, Navigate } from 'react-router-dom'
import { Icon } from '../components/ui/Icon'
import { AnimatedNumber } from '../components/ui/AnimatedNumber'
import { displayDomainValue } from '../lib/display-labels'
import { listChangesIncidents } from '../changes-incidents/changes-incidents-api'
import { MyGovernanceTasks } from '../governance/MyGovernanceTasks'
import { ErrorAlert } from '../portfolio/PortfolioCommon'
import { useTenant } from '../tenancy/TenantProvider'
import { AiRoadmapGrowthChart } from './AiRoadmapGrowthChart'
import { getAiRoadmapGrowth, previewReportPack } from './reporting-api'
import { formatBoardDate, numberValue, record, records, textValue } from './reporting-model'

type AttentionTile = {
  label: string
  count: number
  detail: string
  route: string
  tone?: 'attention' | 'waiting' | 'neutral'
}

function AttentionTileLink({ tile }: { tile: AttentionTile }) {
  return <Link className={`overview-attention-tile ${tile.tone ?? 'neutral'}`} to={tile.route}>
    <strong><AnimatedNumber value={tile.count} /></strong>
    <span>{tile.label}</span>
    <small>{tile.detail}</small>
    <Icon name="arrow-right" size={16} aria-hidden="true" />
  </Link>
}

export function ManagementAttentionDashboard() {
  const { current } = useTenant()
  const organisationId = current?.organisation.id ?? ''
  const dashboardQuery = useQuery({
    queryKey: ['management-attention-dashboard', organisationId],
    queryFn: () => previewReportPack(organisationId, ['executive_overview']),
    enabled: Boolean(organisationId),
    refetchInterval: 5 * 60 * 1000,
  })
  const growthQuery = useQuery({
    queryKey: ['ai-roadmap-growth', organisationId],
    queryFn: () => getAiRoadmapGrowth(organisationId),
    enabled: Boolean(organisationId),
    refetchInterval: 5 * 60 * 1000,
  })
  const changesQuery = useQuery({
    queryKey: ['overview-changes-incidents', organisationId],
    queryFn: () => listChangesIncidents(organisationId),
    enabled: Boolean(organisationId),
    refetchInterval: 5 * 60 * 1000,
  })

  if (!current) return <Navigate to="/workspace/create" replace />

  const payload = dashboardQuery.data?.payload
  const portfolio = record(payload?.portfolio)
  const governance = record(payload?.governance)
  const strategy = record(payload?.strategy)
  const landscape = record(strategy.landscape)
  const landscapeGaps = record(landscape.gaps)
  const priorities = records(landscape.priorities)
  const initiativeIds = new Set(records(landscape.roadmap).map((item) => textValue(item.initiativeId)).filter(Boolean))
  const matters = records(governance.matters)
  const recentEvents = records(governance.recentEvents).slice(0, 5)
  const openMaterialChanges = (changesQuery.data?.items ?? []).filter((item) => item.recordType === 'material_change' && item.status !== 'closed').length
  const openMatters = numberValue(governance.openMatters) || matters.length
  const attentionTiles: AttentionTile[] = [
    { label: 'Governance attention', count: openMatters, detail: 'Recorded matters requiring review', route: '/ai-register?summary=attention', tone: openMatters ? 'attention' : 'neutral' },
    { label: 'Missing owners', count: numberValue(portfolio.missingOwners), detail: 'AI uses without an accountable owner', route: '/ai-register?summary=missing', tone: numberValue(portfolio.missingOwners) ? 'attention' : 'neutral' },
    { label: 'Approval decisions', count: numberValue(governance.awaitingDecisions), detail: 'Awaiting an authorised decision', route: '/actions', tone: numberValue(governance.awaitingDecisions) ? 'waiting' : 'neutral' },
    { label: 'Material changes', count: openMaterialChanges, detail: 'Open records awaiting completion', route: '/changes-incidents?type=material_change&status=open', tone: openMaterialChanges ? 'waiting' : 'neutral' },
    { label: 'Overdue items', count: numberValue(governance.overdueMandatory), detail: 'Mandatory work beyond its due date', route: '/ai-register?summary=attention', tone: numberValue(governance.overdueMandatory) ? 'attention' : 'neutral' },
    { label: 'Unaligned initiatives', count: numberValue(landscapeGaps.initiativeNoAlignment), detail: 'Initiatives not linked to strategy', route: '/strategy', tone: 'neutral' },
  ]

  return <div className="page-stack management-dashboard overview-rebuilt">
    <div className="overview-introduction">
      <header className="management-dashboard-header overview-front-door">
        <svg className="overview-front-door-motif" viewBox="0 0 900 260" preserveAspectRatio="none" aria-hidden="true">
          <path pathLength="1" d="M-40 214C108 92 244 78 382 142s275 90 558-58" />
          <path pathLength="1" d="M-38 242C126 120 256 111 401 169s288 65 548-30" />
          <path pathLength="1" d="M166 282c111-116 248-162 395-126 105 26 199 22 339-46" />
        </svg>
        <div className="overview-front-door-copy">
          <h1>AI governance overview</h1>
          <p>See the organisation&apos;s AI portfolio, strategic alignment, governance position and current priorities.</p>
        </div>
      </header>

      {payload ? <section className="overview-portfolio" aria-labelledby="overview-portfolio-heading">
        <div className="section-heading compact">
          <div><p className="eyebrow">Portfolio summary</p><h2 id="overview-portfolio-heading">AI portfolio at a glance</h2></div>
        </div>
        <div className="overview-portfolio-metrics">
          <Link className="overview-portfolio-tile ai-uses" to="/ai-register">
            <strong><AnimatedNumber value={numberValue(portfolio.totalUses)} duration={600} /></strong>
            <span>AI Uses</span>
            <small>Recorded across the organisation</small>
          </Link>
          <Link className="overview-portfolio-tile ai-initiatives" to="/strategy">
            <strong><AnimatedNumber value={initiativeIds.size} duration={620} /></strong>
            <span>AI Initiatives</span>
            <small>Planned or in progress</small>
          </Link>
          <Link className="overview-portfolio-tile strategic-priorities" to="/strategy">
            <strong><AnimatedNumber value={priorities.length} duration={640} /></strong>
            <span>Strategic Priorities</span>
            <small>Connected to AI delivery</small>
          </Link>
        </div>
      </section> : null}
    </div>
    <ErrorAlert error={dashboardQuery.error ?? growthQuery.error ?? changesQuery.error} />

    {dashboardQuery.isLoading ? <section className="panel management-dashboard-loading"><span className="report-generation-spinner" /><div><strong>Building today’s management position</strong><p>Reconciling governance, portfolio and strategy records…</p></div></section> : null}

    {payload ? <>
      <AiRoadmapGrowthChart growth={growthQuery.data} loading={growthQuery.isLoading} />

      <section className="overview-organisation-attention" aria-labelledby="organisation-attention-heading">
        <div className="section-heading compact"><div><p className="eyebrow">Organisation attention</p><h2 id="organisation-attention-heading">Where attention is needed</h2></div></div>
        <div className="overview-attention-tiles">{attentionTiles.map((tile) => <AttentionTileLink key={tile.label} tile={tile} />)}</div>
      </section>

      <MyGovernanceTasks alwaysShow />

      <section className="panel overview-recent-activity" aria-labelledby="recent-activity-heading">
        <div className="section-heading compact"><div><p className="eyebrow">Recent activity</p><h2 id="recent-activity-heading">Latest governance activity</h2></div><Link className="text-link" to="/reports">View all activity</Link></div>
        {recentEvents.length ? <div className="overview-recent-activity-list">{recentEvents.map((event, index) => <Link key={`${textValue(event.occurredAt)}-${index}`} to={textValue(event.route, '/ai-register')}><span><small>{textValue(event.type, 'Governance activity')}</small><strong>{textValue(event.title, 'Recorded event')}</strong><em>{displayDomainValue(event.detail, 'Detail not recorded')}</em></span><time>{formatBoardDate(event.occurredAt)}</time><Icon name="arrow-right" size={16} aria-hidden="true" /></Link>)}</div> : <p className="management-quiet-state"><Icon name="clock" size={22} /> No recent governance activity is recorded.</p>}
      </section>
    </> : null}
  </div>
}
